import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { formatDateWithMonthYear } from 'utils';
import {
  ActionButtons,
  ConfirmationModal,
  Icon,
  NotificationViewer,
} from '../../../../../components';
import { Button } from '../../../../../components/Button/Button';
import { TaskTracker } from '../../../../../components/TaskTracker/TaskTracker';
import { SEVERITY, VALIDATION_TYPES } from '../../../constants';
import useMilestones from '../../../hooks/useMilestones';
import AddMilestone from './AddMilestone';

import './MilestoneTracker.scss';

/**
 * MilestoneTracker - Manages contract milestones with CRUD operations and validation
 *
 * Features:
 * - Create, read, update, delete milestones
 * - Drag-and-drop reordering with position tracking
 * - Real-time validation (amount mismatch, duplicate dates, chronological order)
 * - Optimistic UI updates with error handling
 * - Confirmation modals for destructive actions
 * - Save confirmation with budget and duration warnings
 *
 * @param {Array} milestones - Array of milestone objects
 * @param {Function} formatCurrency - Function to format currency values
 * @param {Object} contract - Contract object containing start_date and public_id
 * @param {number|string} totalProjectAmount - Total project budget amount
 * @param {Function} refreshData - Function to refresh data after save
 *
 * @returns {JSX.Element} Rendered MilestoneTracker component
 */
const MilestoneTracker = ({
  milestones,
  formatCurrency,
  contract,
  totalProjectAmount,
  refreshData,
  isOwnedByUser,
}) => {
  // Local state for confirmation modal
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    milestone: null,
  });

  // Local state for save confirmation modal
  const [saveModal, setSaveModal] = useState({
    isOpen: false,
    type: null, // 'warning' or 'info'
    message: '',
    totalMilestoneAmount: 0,
    currentDuration: 0,
    newDuration: 0,
    willUpdateDuration: false,
  });

  const {
    // State
    milestoneList,
    deletedMilestones,
    isModalOpen,
    isSaving,
    editingMilestone,
    validationWarnings,
    hasChanges,
    durationInfo,

    // Form controls
    control,
    errors,
    isSubmitting,
    handleSubmit,

    // Actions
    handleEditMilestone,
    handleAddMilestone,
    handleSaveMilestone,
    handleCloseModal,
    handleDeleteMilestone,
    handleSaveContractMilestones,
    handleCancelChanges,
    setInsertPosition,
  } = useMilestones(milestones, contract, totalProjectAmount);

  const [isEditing, setIsEditing] = useState(false);

  // Simplified total amount calculation (no need for memoization for simple reduce)
  const totalMilestoneAmount = milestoneList.reduce((sum, milestone) => {
    const amount = parseFloat(milestone.amount_usd) || 0;
    return sum + amount;
  }, 0);

  // Memoized validation checks
  const hasValidationErrors = useMemo(
    () =>
      validationWarnings.some((warning) => warning.severity === SEVERITY.ERROR),
    [validationWarnings]
  );

  const amountWarning = useMemo(
    () =>
      validationWarnings.find(
        (warning) => warning.type === VALIDATION_TYPES.AMOUNT_MISMATCH
      ),
    [validationWarnings]
  );

  // Use durationInfo directly - no need for additional memoization
  const currentDuration = contract?.expected_duration_days || 0;
  const newDuration = durationInfo.totalDays;

  // Optimized handlers with useCallback
  const handleDeleteClick = useCallback((milestone) => {
    setDeleteConfirmation({
      isOpen: true,
      milestone,
    });
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmation.milestone) {
      handleDeleteMilestone(deleteConfirmation.milestone);
    }
    setDeleteConfirmation({ isOpen: false, milestone: null });
  }, [deleteConfirmation.milestone, handleDeleteMilestone]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmation({ isOpen: false, milestone: null });
  }, []);

  // Handle adding milestone between existing ones
  const handleAddMilestoneBetween = useCallback(
    (insertIndex) => {
      setInsertPosition(insertIndex);
      handleAddMilestone(); // Open the add milestone modal
    },
    [setInsertPosition, handleAddMilestone]
  );

  const handleSaveChangesClick = useCallback(() => {
    if (hasValidationErrors || !milestoneList.length) return;

    const willUpdateDuration =
      newDuration !== currentDuration && newDuration > 0;

    if (amountWarning) {
      setSaveModal({
        isOpen: true,
        type: 'warning',
        message: amountWarning.message,
        totalMilestoneAmount,
        currentDuration,
        newDuration,
        willUpdateDuration,
      });
    } else {
      setSaveModal({
        isOpen: true,
        type: 'info',
        message:
          'All milestone validations passed. You can proceed with saving the changes.',
        totalMilestoneAmount: 0,
        currentDuration,
        newDuration,
        willUpdateDuration,
      });
    }
  }, [
    hasValidationErrors,
    amountWarning,
    totalMilestoneAmount,
    newDuration,
    currentDuration,
    milestoneList.length,
  ]);

  const handleConfirmSave = useCallback(async () => {
    // Use the hook's save function
    const result = await handleSaveContractMilestones();

    if (result.success) {
      // Close save modal
      setSaveModal({
        isOpen: false,
        type: null,
        message: '',
        totalMilestoneAmount: 0,
      });

      // Reset editing state after successful save
      setIsEditing(false);

      // Refresh the data to get the latest milestones from the server
      if (refreshData) {
        await refreshData();
      }
    }
    // If there's an error, the toast notification is already shown in the hook
    // and the modal stays open so user can try again
  }, [handleSaveContractMilestones, refreshData]);

  const handleCancelSave = useCallback(() => {
    setSaveModal({
      isOpen: false,
      type: null,
      message: '',
      totalMilestoneAmount: 0,
    });
  }, []);

  // Extracted render functions for better maintainability and performance
  const renderMilestoneContent = useCallback(
    (milestone, index, status) => {
      const hasMilestoneError = validationWarnings.some(
        (warning) =>
          warning.severity === SEVERITY.ERROR &&
          warning.milestoneId === milestone.id
      );

      const displayStatus = hasMilestoneError ? 'error' : status;

      return (
        <div className={`milestone-content-card ${displayStatus}`}>
          <div className='card-header'>
            <div className='header-main'>
              <span className='task-number'>Milestone {index + 1}</span>
              <h4 className='task-title'>{milestone.title}</h4>
            </div>
            <div className='header-meta'>
              <span className='milestone-amount'>
                {formatCurrency
                  ? formatCurrency(milestone.amount_usd)
                  : `$${milestone.amount_usd}`}
              </span>
              {isOwnedByUser && (
                <ActionButtons
                  actions={[
                    {
                      id: 'edit',
                      icon: 'Edit_Pencil_02',
                      onClick: () => {
                        setIsEditing(true);
                        handleEditMilestone(milestone);
                      },
                      variant: 'primary',
                      className: 'icon--edit',
                    },
                    {
                      id: 'delete',
                      icon: 'Trash_Empty',
                      onClick: () => handleDeleteClick(milestone),
                      variant: 'danger',
                      disabled: milestoneList.length === 1,
                      className: 'icon--delete',
                    },
                  ]}
                  data={milestone}
                  iconSize='md'
                  gap='xs'
                />
              )}
            </div>
          </div>

          {milestone.description && (
            <p className='task-description'>{milestone.description}</p>
          )}

          <div className='card-footer'>
            {milestone.due_date && (
              <div className='meta-item'>
                <Icon name='Calendar' size='sm' />
                <span>Due: {formatDateWithMonthYear(milestone.due_date)}</span>
              </div>
            )}
            {milestone.completed_at && (
              <div className='meta-item completed-date'>
                <Icon name='Check_Circle' size='sm' />
                <span>
                  Completed: {formatDateWithMonthYear(milestone.completed_at)}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    },
    [
      validationWarnings,
      formatCurrency,
      handleEditMilestone,
      handleDeleteClick,
      isOwnedByUser,
    ]
  );

  // Memoized save modal content for better performance
  const renderSaveModalContent = useCallback(
    () => (
      <div className='save-modal-content'>
        {saveModal.type === 'warning' ? (
          <div>
            <div className='budget-details'>
              <p>
                Current Project Budget:
                <strong> ${totalProjectAmount?.toLocaleString()}</strong>
              </p>
              <p>
                Total Milestone Amount:
                <strong>
                  ${saveModal.totalMilestoneAmount.toLocaleString()}
                </strong>
              </p>
              {saveModal.newDuration > 0 && (
                <>
                  <p>
                    Updated Contract Duration:
                    <strong> {saveModal.newDuration} days</strong>
                  </p>
                </>
              )}
              <p className='proceed-warning'>
                <strong>
                  If you proceed, the project budget amount will be updated to
                  (${saveModal.totalMilestoneAmount.toLocaleString()})
                  {saveModal.newDuration > 0 &&
                    ` and contract duration to ${saveModal.newDuration} days`}
                  .
                </strong>
              </p>
              <p>Are you sure you want to proceed?</p>
            </div>
          </div>
        ) : (
          <div>
            {saveModal.newDuration > 0 && (
              <div className='duration-update-info'>
                <p>
                  <strong>Duration Update:</strong>
                </p>
                <p>
                  Contract duration will be updated from{' '}
                  <strong>{saveModal.currentDuration} days </strong> to{' '}
                  <strong>{saveModal.newDuration} days</strong> .
                </p>
              </div>
            )}
            <p>{saveModal.message}</p>
          </div>
        )}
      </div>
    ),
    [saveModal, totalProjectAmount]
  );

  // Memoized action buttons for better performance
  const renderActionButton = useCallback(
    () => (
      <div className='milestone-actions'>
        <div className='milestone-save-actions'>
          <Button
            variant='primary'
            size='md'
            disabled={
              !hasChanges ||
              hasValidationErrors ||
              isSaving ||
              !milestoneList.length
            }
            onClick={handleSaveChangesClick}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    ),
    [
      isEditing,
      handleAddMilestone,
      hasChanges,
      hasValidationErrors,
      isSaving,
      milestoneList.length,
      handleSaveChangesClick,
    ]
  );

  return (
    <>
      <NotificationViewer warnings={validationWarnings} />

      <TaskTracker
        items={milestoneList}
        startDate={contract?.start_date}
        title='Project Progress'
        emptyTitle='No Milestones Defined'
        emptyMessage="This contract doesn't have any milestones tracking yet."
        renderContent={renderMilestoneContent}
        showProgress={contract?.start_date}
        onAddBetween={handleAddMilestoneBetween}
        showAddButton={isOwnedByUser}
        showLastAddButton={isOwnedByUser}
        lastNotAddText='Add new milestone at the end'
      />

      {isOwnedByUser && renderActionButton()}

      {/* Milestone Edit/Add Modal - Extracted to AddMilestone Component */}
      <AddMilestone
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleSubmit(handleSaveMilestone)}
        control={control}
        isEditing={isEditing}
        editingTitle={editingMilestone?.title || ''}
        handleSubmit={handleSubmit(handleSaveMilestone)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        type='error'
        title='Delete Milestone'
        message={
          <p>
            Are you sure you want to delete{' '}
            <strong>"{deleteConfirmation.milestone?.title}"</strong>? This
            action cannot be undone and will remove all associated data.
          </p>
        }
        confirmText='Delete'
        cancelText='Cancel'
        size='md'
        closeOnOverlayClick={false}
      />

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        isOpen={saveModal.isOpen}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        type={saveModal.type === 'warning' ? 'warning' : 'info'}
        title={''}
        confirmText={isSaving ? 'Saving...' : 'Proceed'}
        cancelText='Cancel'
        size='md'
        closeOnOverlayClick={false}
        disabled={isSaving}
        message={renderSaveModalContent()}
      />
    </>
  );
};

MilestoneTracker.propTypes = {
  milestones: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      amount_usd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      description: PropTypes.string,
      due_date: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      is_completed: PropTypes.bool,
      completed_at: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
    })
  ).isRequired,
  formatCurrency: PropTypes.func,
  contract: PropTypes.shape({
    start_date: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    public_id: PropTypes.string,
    expected_duration_days: PropTypes.number,
  }),
  totalProjectAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refreshData: PropTypes.func,
  isOwnedByUser: PropTypes.bool.isRequired,
};

export default MilestoneTracker;
